import { color, layout, spacing, typography } from '$tablet-app/src/app/theme';
import { TinyColor } from '@ctrl/tinycolor';
import React, { forwardRef, useState } from 'react';
import { StyleProp, StyleSheet, TextInput, TextStyle } from 'react-native';
import { Pressable } from 'react-native-web-hover';
import styled from 'styled-components/native';
import NewButton from '../Button/NewButton';
import { Text } from '../Text/Text';
import { TextInputProps } from '../TextInput/TextInput.props';

interface TextInputWithButtonsProps extends TextInputProps {
  onSubmit(text: string): void;
  onCancel?(): void;
  textStyle?: StyleProp<TextStyle>;
}

const TextInputWithButtons = forwardRef<TextInput, TextInputWithButtonsProps>(
  (props, ref) => {
    const {
      preset = 'default',
      error,
      children,
      style: styleOverride,
      textStyle,
      onCancel,
      ...rest
    } = props;

    const [text, setText] = useState('');

    const [borderColor, setBorderColor] = useState<string>(
      preset === 'default' ? color.formElement : color.primaryIcon
    );

    const clear = () => {
      setText('');
      onCancel?.();
    };

    const onBlur = () => setBorderColor(color.formElement);
    const onFocus = () => setBorderColor(color.primaryIcon);

    const onPressSubmit = () => {
      setText('');
      props.onSubmit(text);
    };

    return (
      <>
        <Pressable
          style={({ hovered }) =>
            StyleSheet.flatten([
              styles.buttonContainer,
              styleOverride,
              {
                borderColor: hovered
                  ? new TinyColor(borderColor).darken(20).toHexString()
                  : borderColor,
              },
            ])
          }
        >
          <TextInput
            ref={ref}
            style={[styles.input, textStyle]}
            onChangeText={setText}
            value={text}
            onSubmitEditing={onPressSubmit}
            onBlur={onBlur}
            onFocus={onFocus}
            {...rest}
          />

          {text !== '' ? (
            <Buttons>
              <NewButton
                variant="secondary"
                onPress={clear}
                icon="times"
                size="small"
              />

              <NewButton onPress={onPressSubmit} icon="check" size="small" />
            </Buttons>
          ) : null}
        </Pressable>
        {error && <Text preset="error">{error.message}</Text>}
      </>
    );
  }
);

const Buttons = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-right: 6px;
  margin-left: auto;
  padding-top: 5px;
  padding-bottom: 5px;
`;

// Styles
const styles = StyleSheet.create({
  buttonContainer: {
    borderColor: color.formElement,
    borderWidth: 2,
    borderRadius: 10,
    ...layout.verticallyCenteredRow,
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  input: {
    flex: 1,
    paddingLeft: spacing[4],
    paddingRight: spacing[2],
    color: color.text,
    fontFamily: typography.primary.regular,
    fontSize: 18,
    height: 56,
    maxWidth: '100%',
    minWidth: 150,
  },
});

export default TextInputWithButtons;
